import theme from '@/config/theme';
import Vue from 'vue';
import styled, { css } from 'vue-styled-components';

interface StyleProps {
  width: string;
  height: string;
  maxWidth: string;
  maxHeight: string;
  position: string;
  backgroundColor: string;
  fontSize: string;
  margin: string;
  mt: string;
  ml: string;
  mr: string;
  mb: string;
  padding: string;
  pt: string;
  pr: string;
  pb: string;
  pl: string;
  borderBox: string;
  color: string;
  gap: string;
  rowGap: string;
  columnGap: string;
  fontWeight: string;
  textAlign: string;
  hover: string;
  boxShadow: string;
  display: string;
  justifyContent: string;
  alignItems: string;
  flexDirection: string;
  alignSelf: string;
  flexGrow: string;
  flexWrap: string;
  justifyItems: string;
  alignContent: string;
  justifySelf: string;
  templateColumns: string;
  templateRows: string;
  autoFlow: string;
  autoRows: string;
  autoColumns: string;
  overflow: string;
  overflowX: string;
  overflowY: string;
  zIndex: string;
}

const StyleGlobalProps = {
  width: String,
  height: String,
  position: String,
  backgroundColor: String,
  fontSize: String,
  margin: String,
  padding: String,
  borderBox: String,
  color: String,
  gap: String,
  rowGap: String,
  columnGap: String,
  fontWeight: String,
  mb: String,
  mt: String,
  ml: String,
  mr: String,
  pt: String,
  pr: String,
  pb: String,
  pl: String,
  textAlign: String,
  hover: String,
  boxShadow: String,
  display: String,
  justifyContent: String,
  alignItems: String,
  flexDirection: String,
  alignSelf: String,
  flexGrow: String,
  flexWrap: String,
  justifyItems: String,
  alignContent: String,
  justifySelf: String,
  templateColumns: String,
  templateRows: String,
  autoFlow: String,
  autoRows: String,
  autoColumns: String,
  overflow: String,
  overflowX: String,
  overflowY: String,
  maxWidth: String,
  maxHeight: String,
  zIndex: String,
};

const defaultStyle = (props: StyleProps) => css`
  ${props.display && `display: ${props.display}`};
  ${props.width && `width: ${props.width}`};
  ${props.height && `height: ${props.height}`};
  ${props.maxWidth && `max-width: ${props.maxHeight}`}
  ${props.maxHeight && `max-height: ${props.maxHeight}`}
  ${props.padding && `padding: ${props.padding}`};
  ${props.margin && `margin: ${props.margin}`};
  ${props.borderBox && `box-sizing: ${props.borderBox}`};
  ${props.backgroundColor && `background-color: ${props.backgroundColor}`};
  ${props.fontSize && `font-size: ${props.fontSize}`};
  ${props.color && `color: ${props.color}`};
  ${props.position && `position: ${props.position}`};
  ${props.gap && `gap: ${props.gap}`}
  ${props.rowGap && `row-gap: ${props.rowGap}`}
  ${props.columnGap && `column-gap: ${props.columnGap}`}
  ${props.fontWeight && `font-weight: ${props.fontWeight}`}
  ${props.textAlign && `text-align: ${props.textAlign}`}
  ${props.boxShadow && `box-shadow: ${props.boxShadow}`}
  ${props.mt && `margin-top: ${props.mt}`}
  ${props.mr && `margin-right: ${props.mr}`}
  ${props.mb && `margin-bottom: ${props.mb}`}
  ${props.ml && `margin-left: ${props.ml}`}
  ${props.pt && `padding-top: ${props.pt}`}
  ${props.pr && `padding-right: ${props.pr}`}
  ${props.pb && `padding-bottom: ${props.pb}`}
  ${props.pl && `padding-left: ${props.pl}`}
  ${props.flexDirection && `flex-direction: ${props.flexDirection}`};
  ${props.justifyContent && `justify-content: ${props.justifyContent}`};
  ${props.alignItems && `align-items: ${props.alignItems}`};
  ${props.alignSelf && `align-self: ${props.alignSelf}`};
  ${props.flexGrow && `flex-grow: ${props.flexGrow}`};
  ${props.flexWrap && `flex-wrap: ${props.flexWrap}`};
  ${props.justifyItems && `justify-items: ${props.justifyItems}`};
  ${props.alignContent && `align-content: ${props.alignContent}`};
  ${props.justifySelf && `justify-self: ${props.justifySelf}`};
  ${props.templateColumns && `grid-template-columns: ${props.templateColumns}`};
  ${props.templateRows && `grid-template-rows: ${props.templateRows}`};
  ${props.autoFlow && `grid-auto-flow: ${props.autoFlow}`};
  ${props.autoRows && `grid-auto-rows: ${props.autoRows}`};
  ${props.autoColumns && `grid-auto-columns: ${props.autoColumns}`};
  ${props.overflow && `overflow: ${props.overflow}`}
  ${props.overflowX && `overflow-x: ${props.overflowX}`}
  ${props.overflowY && `overflow-y: ${props.overflowY}`}
  ${props.zIndex && `z-index: ${props.zIndex}`}
  ${
  props.hover
    && `:hover {
        ${props.hover}
      }`
}
`;

const Container = styled('div', StyleGlobalProps)`
  ${(props: StyleProps) => defaultStyle(props)}
`;

const Flex = styled(Container)`
  display: flex;
`;

const Grid = styled(Container)`
  display: grid;
`;

const Form = styled('form', StyleGlobalProps)`
  ${(props: StyleProps) => defaultStyle(props)}
`;

const Title = styled('h1', StyleGlobalProps)`
  font-size: ${(props) => props.fontSize ?? '2rem'};
  ${(props: StyleProps) => defaultStyle(props)}
`;

const SubTitle = styled('h2', StyleGlobalProps)`
  font-size: ${(props) => props.fontSize ?? '1.5rem'};
  ${(props: StyleProps) => defaultStyle(props)}
`;

const Description = styled('p', StyleGlobalProps)`
  ${(props: StyleProps) => defaultStyle(props)}
`;

const components = {
  Container,
  Flex,
  Grid,
  Title,
  SubTitle,
  Description,
  Form,
};

Vue.prototype.theme = theme;

Object.entries(components).forEach(([name, component]) => Vue.component(name, component));
